/**
 * Country-flag chip filter row above the Browse map. Derives the country list from the
 * current `/cars/map` payload (so unsupported countries are never shown) and emits a
 * single string `country_code` (e.g. "FR") OR null when the user resets to "All".
 *
 * Visuals: Apple-Music-style scrolling pill row. Active chip is filled black, inactive
 * are bordered with the country flag emoji + count badge.
 */
import { useMemo } from "react";

// Tiny ISO-3166-1 alpha-2 → flag emoji helper. Works because each ASCII A–Z letter has a
// 1-1 mapping to a Unicode regional-indicator letter (offset 0x1F1E6 from 'A').
const flagFor = (cc) => {
  if (!cc || cc.length !== 2) return "🌍";
  return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 0x1f1a5 + c.charCodeAt(0)));
};

export default function CountryFlagChips({ pins, selected, onChange }) {
  const counts = useMemo(() => {
    const m = new Map();
    for (const p of pins || []) {
      const key = p.country_code || "_other";
      const cur = m.get(key) || { code: p.country_code || null, name: p.country_name || "Other", count: 0 };
      cur.count += 1;
      m.set(key, cur);
    }
    return Array.from(m.values()).sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name));
  }, [pins]);

  if (counts.length === 0) return null;
  const total = (pins || []).length;

  return (
    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="country-flag-chips">
      <Chip
        active={!selected}
        onClick={() => onChange(null)}
        testId="country-chip-all"
      >
        <span className="text-base">🌍</span>
        <span>All countries</span>
        <span className="text-[10px] tabular-nums opacity-80">{total}</span>
      </Chip>
      {counts.map((c) => (
        <Chip
          key={c.code || "_other"}
          active={selected === c.code}
          onClick={() => onChange(c.code)}
          testId={`country-chip-${c.code || "other"}`}
        >
          <span className="text-base">{flagFor(c.code)}</span>
          <span>{c.name}</span>
          <span className="text-[10px] tabular-nums opacity-80">{c.count}</span>
        </Chip>
      ))}
    </div>
  );
}

function Chip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? "bg-neutral-900 text-white border-neutral-900"
          : "bg-white border-border text-neutral-700 hover:border-neutral-400"
      }`}
    >
      {children}
    </button>
  );
}
